import { isEventKey } from "keycode";
import { getKeyboardFocusableElements, SetGet } from "../utils";

class FocusTrap extends SetGet {
  constructor(context) {
    super();

    this.contexts = [context];
    this.focusFirstElement = null;
    this.focusBackElement = null;
  }

  get focusableContextElements() {
    return this.contexts.map((context) =>
      getKeyboardFocusableElements(context),
    );
  }

  get allFocusableElements() {
    return this.focusableContextElements.reduce((acc, focusableElements) => {
      return [...acc, ...focusableElements];
    }, []);
  }

  init(
    { focusFirstElement, focusBackElement } = {
      focusFirstElement: null,
      focusBackElement: null,
    },
  ) {
    this.focusFirstElement = focusFirstElement;
    this.focusBackElement = focusBackElement;
    this.tabHandler = this.tabHandler.bind(this);

    this.focus(this.focusFirstElement);
    this.subscribe();
  }

  suspend() {
    this.focus(this.focusBackElement);
    this.unsubscribe();
  }

  subscribe() {
    this.contexts.forEach((context) => {
      context.addEventListener("keydown", this.tabHandler);
    });
  }

  unsubscribe() {
    this.contexts.forEach((context) => {
      context.removeEventListener("keydown", this.tabHandler);
    });
  }

  tabHandler(event) {
    if (!isEventKey(event, "tab")) {
      return;
    }

    this.focusTrap(event);
  }

  appendContext(context) {
    this.contexts = [...this.contexts, context];

    return this;
  }

  prependContext(context) {
    this.contexts = [context, ...this.contexts];

    return this;
  }

  focus(element) {
    if (element) {
      element.focus();
    }

    return element;
  }

  focusTrap(event) {
    // Elements might be shown / hidden repetitively at any point
    // so we ensure to rebuild the focusable elements list on each key event
    const firstSentinel = this.allFocusableElements[0];
    const lastSentinel =
      this.allFocusableElements[this.allFocusableElements.length - 1];

    // If one of the focus trap contexts sentinels has been reached
    // determine which element should gain focus next
    const nextFocusTarget = this.contexts.reduce((acc, context, index) => {
      const currentContextElements = this.focusableContextElements[index];
      const currentContextFirstSentinel = currentContextElements[0];
      const currentContextLastSentinel =
        currentContextElements[currentContextElements.length - 1];

      if (acc !== null) {
        // Next focus target condition has already been met, no need to process any further
        return acc;
      }

      if (event.shiftKey) {
        if (document.activeElement === firstSentinel) {
          // Shift + Tab + first focusable element
          // => jump to last element in focus loop
          return lastSentinel;
        }
        if (document.activeElement === currentContextFirstSentinel) {
          const prevContextElements = this.focusableContextElements[index - 1];
          const prevContextLastSentinel =
            prevContextElements[prevContextElements.length - 1];

          // Shift + Tab + first focusable element of current context
          // => jump to last element of previous context
          return prevContextLastSentinel;
        }
      } else {
        if (document.activeElement === lastSentinel) {
          // Tab + last focusable element
          // => jump to first element in focus loop
          return firstSentinel;
        }
        if (document.activeElement === currentContextLastSentinel) {
          const nextContextElements = this.focusableContextElements[index + 1];
          const nextContextFirstSentinel = nextContextElements[0];

          // Tab + last focusable element of current context
          // => jump to first element of next context
          return nextContextFirstSentinel;
        }
      }

      return acc;
    }, null);

    if (nextFocusTarget !== null) {
      event.preventDefault();
      nextFocusTarget.focus();
    }

    return nextFocusTarget;
  }
}

export { FocusTrap };
